import {
  Box,
  Button,
  Grid,
  Heading,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useMediaQuery,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useCustomToast } from "../reusables";
import { Image, Tooltip } from "../reusables";
import {
  RiBookmarkLine,
  RiPlayListLine,
  RiShareForwardFill,
  RiThumbDownFill,
  RiThumbDownLine,
  RiThumbUpFill,
  RiThumbUpLine,
} from "react-icons/ri";
import {
  BiChevronDown,
  BiDotsVertical,
  BiPencil,
  BiPlay,
  BiPlus,
  BiTrash,
} from "react-icons/bi";
import { useLocation, useNavigate } from "react-router";
import usePosts from "../../hooks/data/usePosts";
import useUserStore from "../../store/userStore";
import { shallow } from "zustand/shallow";
import useColorAccent from "../../hooks/useColorAccent";
import dayjs from "../../helpers/dayJs";
import { FiClock, FiDownload } from "react-icons/fi";
import AnimatedNumber from "react-animated-numbers";
import { IoBug } from "react-icons/io5";
import useCopy from "../../helpers/useCopy";
import { useReportManager } from "../../wrappers/report";
import usePlaylist from "../../hooks/data/usePlaylist";
import useReactiveQuery from "../../hooks/data/useReactiveQuery";
import useImageCompressor from "../../hooks/useImageCompressor";
import TextEditorParser from "../textEditor/textEditorParser";
import { useTracker } from "../../wrappers/tracker";
import User from "../user";
import { useHlsAudioActions } from "../../wrappers/hlsAudio";
import useReactions from "../../hooks/data/useReactions";
import { httpsCallable } from "firebase/functions";
import { useFirebase } from "../../wrappers/firebase";
import licenseTypes from "../../assets/licenseTypes.json";

/**
 *
 * @param {import("@chakra-ui/react").GridProps & {id: string}} props
 *
 */
export default function IsolatedPost(props) {
  const { id, ...styleProps } = props;
  const [isMobile] = useMediaQuery("(max-width: 600px)");

  const { attachImage, accentColor, counterColor } = useColorAccent();

  const { playTrack } = useHlsAudioActions();

  const { get, getAll } = useReactiveQuery();

  const post = get(["posts", props.id]);

  const userPlaylists = getAll(["playlists", post?.user?.username], {
    transform: (playlists) =>
      playlists.filter(
        (playlist) =>
          !(
            typeof playlist.get === "function" &&
            typeof playlist.data === "function"
          )
      ),
  });

  const { copy } = useCopy();
  const { deletePost } = usePosts();
  const {
    addLikeReaction,
    removeLikeReaction,
    addDislikeReaction,
    removeDislikeReaction,
  } = useReactions({
    type: "post",
    contentId: post?.id,
    contentUserId: post?.user?.id,
  });

  const { triggerReportContent, triggerClaimContribution } = useReportManager();
  const { addItemToPlaylist } = usePlaylist();

  // const [userPlaylists, setUserPlaylists] = useState();
  const {
    username: stateUsername,
    id: stateUserId,
    stripeCustomerId,
  } = useUserStore((state) => state.user, shallow);

  const toast = useCustomToast();

  const isOwner = post?.user?.username === stateUsername;
  const navigate = useNavigate();
  const [isDeleteOpen, setDeleteOpen] = useState();
  const { findClosestMatch } = useImageCompressor();

  const imageContainerRef = useRef();
  const { trackInteraction } = useTracker();
  const { functions } = useFirebase();
  const [checkoutModal, setCheckoutModal] = useState({
    license: null,
    isOpen: false,
    isBusy: false,
  });
  const [isLicenseBtnBusy, setIsLicenseBtnBusy] = useState(false);

  const location = useLocation();

  useEffect(() => {
    if (post && location.state) {
      console.log("Fired..");
      if (location.state?.autoPlay) handleTrackPlay();
      trackInteraction({
        queryId: location.state?.queryId,
        objectId: post.id,
        interactionType: "view",
      });
    }
  }, [post, location.state]);

  async function updateLikeReaction() {
    if (post.isLikedByUser) {
      removeLikeReaction();
    } else {
      addLikeReaction();
      trackInteraction({
        queryId: location.state?.queryId,
        objectId: post.id,
        interactionType: "reaction-like",
      });
    }
  }

  async function updateDislikeReaction() {
    if (post.isDislikedByUser) {
      removeDislikeReaction();
    } else {
      addDislikeReaction();
      trackInteraction({
        queryId: location.state?.queryId,
        objectId: post.id,
        interactionType: "reaction-dislike",
      });
    }
  }

  async function addToPlaylist(playlistId) {
    await addItemToPlaylist({
      playlistId,
      username: post?.user?.username,
      postId: post.id,
    });
    toast({
      title: "Added to playlist",
      description: "Added this track to your playlist",
      status: "success",
    });
  }

  function goToEdit() {
    navigate("/create/post", {
      state: { post },
    });
  }

  const formattedDate =
    post?.createdAt && post?.createdAt?.toDate
      ? dayjs(post?.createdAt.toDate()).calendar()
      : "";

  const thumbnailFileName = useMemo(
    () =>
      findClosestMatch(post?.track?.thumbnails, imageContainerRef)?.fileName,
    [post?.track?.thumbnails, imageContainerRef.current]
  );

  function toggleDelete() {
    setDeleteOpen((prev) => !prev);
  }

  function performDelete() {
    deletePost({ postId: post.id });
    navigate("/");
  }

  async function goToCheckout(license) {
    setCheckoutModal((prev) => ({ ...prev, isBusy: true }));
    const isOpenLicense = !!licenseTypes.find(
      ({ title, type }) =>
        title.toLowerCase() === license?.title?.toLowerCase() &&
        type !== "commercial"
    );

    if (isOpenLicense) {
      const data = {
        license: license?.title,
        ownerId: post.user.id,
        postId: post.id,
      };
      const licenseGenFunc = httpsCallable(functions, "generateOpenLicense");
      try {
        await licenseGenFunc(data);
        toast({
          status: "success",
          title: "Successfully purchased track",
          description: 'Visit "My Orders" to downlaod the track.',
        });
        setCheckoutModal({ isOpen: false });
      } catch (ex) {
        toast({
          status: "error",
          title: "Could not go to checkout",
          description: "Something went wrong! Please try again later.",
        });
      }
    } else {
      const data = {
        success_url: window.location.href,
        stripeCustomerId,
        productId: post.stripeProductId,
        license,
        metadata: {
          sellerUid: post.user.id,
          postId: post.id,
        },
      };

      const checkoutFunc = httpsCallable(functions, "createLicenseCheckout");
      try {
        const { data: checkoutObj } = await checkoutFunc(data);
        window.open(checkoutObj.url, "_self");
      } catch (ex) {
        console.log("Checkout error is:", ex);
        toast({
          status: "error",
          title: "Could not go to checkout",
          description: "Something went wrong! Please try again later.",
        });
      }
    }
    setCheckoutModal((prev) => ({ ...prev, isBusy: false }));
  }

  function toggleCheckoutModal() {
    if (!checkoutModal.isBusy)
      setCheckoutModal((prev) => ({ ...prev, isOpen: !prev.isOpen }));
  }

  async function downloadTrackFile() {
    setIsLicenseBtnBusy(true);
    const downloadFileFunc = httpsCallable(functions, "downloadAudioFile");

    try {
      const result = await downloadFileFunc({
        postId: post.id,
        ownerId: post.user.id,
      });
      if (!result.data) {
        toast({
          title: "Something went wrong",
          description: "Try downloading again, later",
          status: "error",
        });
      } else {
        window.open(result.data, "_blank");
      }
    } catch (ex) {
      console.error("File download error:", ex);
      toast({
        title: "Something went wrong",
        description: "Try downloading again, later",
        status: "error",
      });
    }
    setIsLicenseBtnBusy(false);
  }

  async function handleTrackPlay() {
    navigate(location.pathname, {
      state: {
        ...location.state,
        autoPlay: false,
      },
    });
    playTrack(post);
    trackInteraction({
      queryId: location.state?.queryId,
      objectId: post.id,
      interactionType: "play",
    });
    const streamFunc = httpsCallable(functions, "handleStreamCount");
    await streamFunc({
      ownerId: post.user.id,
      postId: post.id,
    });
  }

  return (
    <VStack w="full">
      {isOwner && (
        <HStack
          w="full"
          justify={"space-between"}
          align={"center"}
          bg="#252525"
          border="1px solid #3e3e46"
          borderRadius={"md"}
          px="2"
          pr="1"
          py="1"
        >
          <Text fontSize={"xs"} color={"gray.400"}>
            You can modify this post
          </Text>
          <Menu placement="bottom">
            <MenuButton
              as={Button}
              size="sm"
              // icon={<BiPencil />}
              variant="nav"
              fontSize={"xs"}
              rightIcon={<BiDotsVertical />}
              border="1px solid #3e3e46"
            >
              Actions
            </MenuButton>
            <MenuList>
              <MenuItem onClick={goToEdit}>
                <BiPencil />
                <Text>Edit</Text>
              </MenuItem>
              <MenuItem color="red.400" onClick={toggleDelete}>
                <BiTrash />
                <Text>Delete post</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      )}
      <Grid
        w="full"
        h="max-content"
        // gridTemplateColumns={"70% 30%"}
        gridTemplateColumns={!isMobile && "minmax(0, 0.6fr) minmax(0, 0.4fr)"}
        gridColumnGap={"3"}
        gridRowGap={"3"}
        gridTemplateRows={
          isMobile ? "260px repeat(3, auto)" : "260px auto auto"
        }
        overflow={"visible"}
        transition={"all 350ms"}
        {...styleProps}
        // overflowX={"hidden"}
      >
        <Box h="260px" pos="relative" gridArea={"1 / 1 / 2 / 2"} minW={"0"}>
          <Image
            ref={imageContainerRef}
            firebaseStoragePath={
              thumbnailFileName
                ? `photos/${post?.user?.id}/${post?.id}/thumbnails/${thumbnailFileName}`
                : null
            }
            borderRadius={"lg"}
            w="full"
            h="260px"
            minW="0"
            onLoad={attachImage}
          />
          <VStack
            pos="absolute"
            top="0"
            bottom={"0"}
            left={"0"}
            right={"0"}
            justify={"center"}
            align={"center"}
            _hover={{
              bg: "#00000040",
            }}
            onClick={handleTrackPlay}
            cursor={"pointer"}
          >
            <IconButton
              size="lg"
              borderRadius={"full"}
              icon={<BiPlay size={30} />}
              boxShadow={"0px 0px 20px 5px #00000070"}
            />
            <Text
              fontSize={"12px"}
              display={"flex"}
              px="1"
              py="0.5"
              gap="2"
              alignItems={"center"}
              bg={`${accentColor}70`}
              backdropFilter={"blur(5px)"}
              borderRadius={"md"}
              color={counterColor}
              fontWeight={"bold"}
              as="div"
            >
              <FiClock />
              {dayjs.utc(post?.duration * 1000).format("mm:ss")}
            </Text>
          </VStack>
        </Box>
        <Grid
          gridArea={isMobile ? "4 / 1 / 5 / 2" : "1 / 2 / 2 / 3"}
          bg="#1a1a1d"
          //   border="1px solid #3e3e46"
          borderRadius={"lg"}
          h="full"
          minW={!isMobile && "0px"}
          p="2"
          //   px="3"
          pr="1"
          // pb="0"
          align={"flex-start"}
          spacing={"3.5"}
          pos="relative"
          gridTemplateRows={"auto 1fr auto"}
          rowGap={"4"}
        >
          <HStack w="full" justify={"space-between"} align={"flex-start"}>
            <VStack spacing={"0"} align={"flex-start"}>
              <Box fontWeight={"semibold"} fontSize={"xl"}>
                <AnimatedNumber
                  animateToNumber={post?.streamCount || 0}
                  transitions={(index) => ({
                    duration: 0.6,
                  })}
                  useThousandsSeparator
                />
              </Box>
              <Text fontSize={"12px"} color="gray.300">
                Streams
              </Text>
            </VStack>
          </HStack>

          <VStack w="full" spacing={"1"} align={"flex-start"}>
            <Text fontSize={"12px"} color={"gray.300"} fontWeight={"semibold"}>
              Contributors
            </Text>
            <HStack
              w="full"
              justify={"flex-start"}
              flexWrap={"wrap"}
              spacing={"1"}
            >
              {post?.contributors?.map((contributor, index) => {
                return (
                  <User.Container
                    user={{
                      ...contributor,
                      allowNavigate: true,
                    }}
                    key={`post_contributor_user_item_uid_${contributor.username}`}
                    preventFetch={true}
                  >
                    <Tooltip
                      label={
                        <VStack align={"center"} spacing={"0"}>
                          <Text>@{contributor.username}</Text>
                          <Text>{contributor.role}</Text>
                        </VStack>
                      }
                      placement="bottom"
                    >
                      <User.Image h="35px" w="35px" />
                    </Tooltip>
                  </User.Container>
                );
              })}

              {(!post?.contributors || post?.contributors?.length <= 0) && (
                <Text
                  fontSize={"xs"}
                  fontWeight={"medium"}
                  color="gray.400"
                  w="full"
                >
                  No additional contributors
                </Text>
              )}
              {!isOwner && !isMobile && (
                <Tooltip
                  placement="bottom"
                  label={<Text>Claim contribution</Text>}
                >
                  <IconButton
                    //   as="button"
                    variant={"nav"}
                    h="35px"
                    w="35px"
                    p="0 !important"
                    minW={"0"}
                    border={"1px dashed"}
                    borderColor={"gray.600"}
                    borderRadius={"full"}
                    icon={<BiPlus />}
                    cursor={"pointer"}
                    transition={"none"}
                    onClick={() => triggerClaimContribution(post)}
                  />
                </Tooltip>
              )}
            </HStack>
          </VStack>
          {(post?.appliedLicenses?.some(({ price }) => price > 0) ||
            post?.allowFreeDownload) && (
            <Menu>
              <MenuButton
                as={Button}
                w="full"
                rightIcon={<BiChevronDown size={18} />}
                size="sm"
                // variant={"nav"}
                // isDisabled={isOwner}
                border="1px solid"
                borderColor={"gray.700"}
                isLoading={isLicenseBtnBusy}
              >
                Get This Track
              </MenuButton>

              <MenuList w="300px">
                {post?.allowFreeDownload && (
                  <MenuItem
                    h="max-content"
                    borderBottom={"1px solid"}
                    borderBottomColor={"gray.700"}
                    onClick={downloadTrackFile}
                  >
                    <VStack w={"full"} align={"flex-start"}>
                      <HStack align={"center"}>
                        <FiDownload size={14} />

                        <Text
                          fontSize={"13px"}
                          fontWeight={"semibold"}
                          noOfLines={"2"}
                        >
                          Free Download
                        </Text>
                      </HStack>
                      <Text fontSize={"12px"} color="gray.400">
                        Download the raw file of this track.{" "}
                      </Text>
                    </VStack>
                  </MenuItem>
                )}
                {post?.appliedLicenses?.length > 0 && (
                  <Text
                    textTransform={"capitalize"}
                    fontSize={"13px"}
                    fontWeight={"semibold"}
                    color="gray.300"
                    px="3"
                    py="2"
                  >
                    Licenses
                  </Text>
                )}
                {post?.appliedLicenses
                  ?.sort((a, b) => a.price - b.price)
                  ?.map((license) => {
                    return (
                      <MenuItem
                        key={`license_single_${license.title}`}
                        h="max-content"
                        borderBottom={"1px solid"}
                        borderBottomColor={"gray.700"}
                        onClick={() =>
                          setCheckoutModal({ license, isOpen: true })
                        }
                      >
                        <VStack w={"full"} align={"flex-start"}>
                          <Text
                            fontSize={"13px"}
                            fontWeight={"semibold"}
                            noOfLines={"2"}
                          >
                            {license?.title}
                          </Text>
                          <Text fontSize={"12px"} color="gray.400">
                            {license?.buyerDescription}
                          </Text>
                          {license.price && (
                            <Text fontSize={"sm"} fontWeight={"semibold"}>
                              Price: ${license?.price}
                            </Text>
                          )}
                        </VStack>
                      </MenuItem>
                    );
                  })}
              </MenuList>
            </Menu>
          )}
        </Grid>
        <VStack
          gridArea={isMobile ? "2 / 1 / 3 / 2" : "2 / 1 / 3 / 3"}
          borderRadius={"md"}
          w="full"
          align={"flex-start"}
          py="1"
          spacing={isMobile ? "2" : "4"}
          minW={"0"}
        >
          <VStack w="full" align={"flex-start"} color={counterColor}>
            <Heading as={"h1"} fontSize={"xl"} fontWeight={"bold"}>
              {post?.title}
            </Heading>
          </VStack>
          <HStack justify={"space-between"} w="full">
            <User.Regular
              {...post?.user}
              allowNavigate={true}
              extra={<>- {formattedDate}</>}
            />

            <HStack
              borderRadius={"full"}
              overflow={"hidden"}
              maxW="fit-content"
            >
              <Button
                variant={"nav"}
                cursor={"pointer"}
                borderRadius={"full"}
                onClick={updateLikeReaction}
                fontSize={"sm"}
                bg={`${accentColor}50`}
                _hover={{
                  bg: `${accentColor}70`,
                }}
                aspectRatio={
                  !post?.likeCount || post?.likeCount <= 0 ? "1 / 1" : "auto"
                }
              >
                <HStack justify={"center"}>
                  {post?.isLikedByUser ? <RiThumbUpFill /> : <RiThumbUpLine />}
                  {post?.likeCount && <Text>{post?.likeCount}</Text>}
                </HStack>
              </Button>
              {!isMobile && (
                <>
                  <IconButton
                    variant={"nav"}
                    cursor={"pointer"}
                    borderRadius={"full"}
                    onClick={updateDislikeReaction}
                    icon={
                      post?.isDislikedByUser ? (
                        <RiThumbDownFill />
                      ) : (
                        <RiThumbDownLine />
                      )
                    }
                    bg={`${accentColor}50`}
                    _hover={{
                      bg: `${accentColor}70`,
                    }}
                  />
                  <Tooltip label={"Share"} placement="bottom">
                    <IconButton
                      variant={"nav"}
                      cursor={"pointer"}
                      borderRadius={"full"}
                      // onClick={updateDislikeReaction}
                      icon={<RiShareForwardFill />}
                      bg={`${accentColor}50`}
                      _hover={{
                        bg: `${accentColor}70`,
                      }}
                      onClick={() => copy(window.location.href)}
                    />
                  </Tooltip>
                  <Menu placement="left">
                    <Tooltip label={"Save to playlist"} placement="bottom">
                      <MenuButton
                        as={IconButton}
                        variant={"nav"}
                        cursor={"pointer"}
                        borderRadius={"full"}
                        // onClick={updateDislikeReaction}
                        icon={<RiBookmarkLine />}
                        bg={`${accentColor}50`}
                        _hover={{
                          bg: `${accentColor}70`,
                        }}
                      />
                    </Tooltip>

                    <MenuList maxH={"240px"} overflow={"scroll"}>
                      <VStack
                        w="full"
                        h="max-content"
                        spacing={0}
                        align={"flex-start"}
                      >
                        <Text
                          fontSize={"xs"}
                          fontWeight={"semibold"}
                          px="2.5"
                          py="2"
                        >
                          Select Playlist
                        </Text>
                        {userPlaylists?.map((pl) => (
                          <MenuItem
                            key={`playlist_user_${pl.id}`}
                            onClick={() => addToPlaylist(pl.id)}
                          >
                            <RiPlayListLine />
                            {pl.name}
                          </MenuItem>
                        ))}
                      </VStack>
                    </MenuList>
                  </Menu>
                  {!isOwner && (
                    <Menu placement="bottom-end">
                      <MenuButton
                        as={IconButton}
                        icon={<BiDotsVertical />}
                        variant={"nav"}
                        bg={`${accentColor}50`}
                        borderRadius={"full"}
                      />
                      <MenuList>
                        <MenuItem
                          color="red.400"
                          onClick={triggerReportContent}
                        >
                          <IoBug />
                          <Text>Report this content</Text>
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  )}
                </>
              )}
            </HStack>
          </HStack>
        </VStack>
        <Box
          gridArea={isMobile ? "3 / 1 / 4 / 2" : "3 / 1 / 4 / 3"}
          px={isMobile ? "1.5" : "0"}
          minW={"0"}
          overflowX={"hidden"}
        >
          <TextEditorParser
            text={post?.description}
            validSearchResults={post?.description_validSearchResults}
          />
        </Box>
        {isOwner && (
          <Modal size="sm" isOpen={isDeleteOpen} onClose={toggleDelete}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                Delete this post?
                <ModalCloseButton />
              </ModalHeader>
              <ModalBody px="2" fontSize={"sm"}>
                Are you sure you want to delete this post? This cannot be
                undone.
              </ModalBody>
              <ModalFooter>
                <Button size="sm" variant={"nav"} onClick={toggleDelete}>
                  Cancel
                </Button>
                <Button size="sm" onClick={performDelete}>
                  Delete
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}
        <Modal
          size={"md"}
          isOpen={checkoutModal.isOpen}
          isCentered
          onClose={toggleCheckoutModal}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              Note before purchase
              <ModalCloseButton />
            </ModalHeader>
            <ModalBody
              color={"white"}
              fontSize={"sm"}
              px="2"
              as={VStack}
              align={"flex-start"}
              spacing={"3"}
            >
              <Text>
                After you click "Proceed", you will be redirected to the
                checkout page, secured by Stripe. Upon successful payment, you
                will find the purchased items in "My Purchases".
              </Text>
              <Text
                alignItems={"center"}
                gap="2"
                bg="#ECC94B30"
                color="orange.200"
                py="5px"
                px="10px"
                borderRadius={"md"}
                fontSize={"12px"}
                w="full"
              >
                For <b>non-commercial or open licenses</b>, you will receive the
                license immediately without any <b>paid checkout.</b>
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button
                size="sm"
                variant={"nav"}
                onClick={toggleCheckoutModal}
                isDisabled={checkoutModal.isBusy}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => goToCheckout(checkoutModal.license)}
                isLoading={checkoutModal.isBusy}
              >
                Proceed
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Grid>
    </VStack>
  );
}
